import type { ReferencePricing } from "./types";
import { CLI_RENDER_COST_MICRODOLLARS } from "../constants";
import { estimateStepCost, isModelKey } from "./helpers";
import { modelRegistry } from "./registry";

type PricingUnit =
  | "per_second"
  | "per_image"
  | "per_character"
  | "per_render"
  | "per_generation"
  | "per_million_input_tokens"
  | "variable";

type ModelPricingEntry = {
  model: string;
  label: string;
  description: string;
  stepType: string;
  pricing: PricingDetail[];
  referencePricing?: ReferencePricing;
};

type PricingDetail = {
  unit: PricingUnit;
  variant?: string;
  unitPriceMicrodollars: number;
  unitPriceUsd: string;
  credits: number;
  note?: string;
};

type EditorPricingEntry = {
  type: "editor_render";
  label: string;
  pricing: PricingDetail;
};

function formatUsd(microdollars: number): string {
  return `$${(microdollars / 1_000_000).toFixed(6)}`;
}

function pricingEntry(
  unit: PricingUnit,
  microdollars: number,
  opts?: { variant?: string; note?: string },
): PricingDetail {
  const rounded = Math.round(microdollars);
  return {
    unit,
    ...(opts?.variant ? { variant: opts.variant } : {}),
    unitPriceMicrodollars: rounded,
    unitPriceUsd: formatUsd(microdollars),
    credits: rounded,
    ...(opts?.note ? { note: opts.note } : {}),
  };
}

function getPreferredProvider(
  providers: {
    provider: string;
    costPerSecond?: number;
    pricingUnit?: "per_second" | "per_generation";
    estimateCost?: (p: Record<string, unknown>) => number;
  }[],
) {
  return (
    providers.find((entry) => entry.provider === "fal") ??
    providers.find((entry) => entry.provider === "openai") ??
    providers.find((entry) => entry.provider === "kling") ??
    providers.find((entry) => entry.provider === "runware") ??
    providers[0]
  );
}

function estimateVideoPricing(args: {
  binding: {
    estimateCost?: (p: Record<string, unknown>) => number;
    pricingUnit?: "per_second" | "per_generation";
  };
  definition: (typeof modelRegistry)[keyof typeof modelRegistry];
  model: string;
  params?: Record<string, string>;
}): number {
  const pricingUnit = args.binding.pricingUnit ?? "per_second";

  if (pricingUnit === "per_generation") {
    if (typeof args.binding.estimateCost !== "function") {
      return 0;
    }

    try {
      const totalCostMicrodollars = Number(
        args.binding.estimateCost({
          ...getDefaultVideoPricingParams(args.definition, args.params),
          prompt: "test",
        }),
      );

      return Math.max(0, totalCostMicrodollars);
    } catch {
      return 0;
    }
  }

  return estimateStepCost({
    model: args.model,
    type: "video",
    params: getDefaultVideoPricingParams(args.definition, args.params),
    prompt: "test",
  });
}

/** Extract variant option items (resolution, quality) from a model's spec. */
function getOptionItems(
  definition: (typeof modelRegistry)[keyof typeof modelRegistry],
  key: string,
): readonly { label: string; value: string }[] | null {
  const options = definition.spec.options as
    | Record<string, { items?: readonly { label: string; value: string }[] }>
    | undefined;
  const items = options?.[key]?.items;
  return items && items.length > 0 ? items : null;
}

function getDefaultVideoPricingParams(
  definition: (typeof modelRegistry)[keyof typeof modelRegistry],
  overrides?: Record<string, string>,
): Record<string, string> {
  const aspectRatio = getOptionItems(definition, "aspectRatio")?.[0]?.value;
  const duration = getOptionItems(definition, "duration")?.[0]?.value;
  const resolution = getOptionItems(definition, "resolution")?.[0]?.value;
  const quality = getOptionItems(definition, "quality")?.[0]?.value;

  return {
    ...(aspectRatio ? { aspectRatio } : {}),
    ...(duration ? { duration } : {}),
    ...(resolution ? { resolution } : {}),
    ...(quality ? { quality } : {}),
    ...(overrides ?? {}),
  };
}

function extractModelPricing(
  model: string,
  definition: (typeof modelRegistry)[keyof typeof modelRegistry],
): PricingDetail[] {
  const binding = getPreferredProvider(
    definition.providers as unknown as {
      provider: string;
      costPerSecond?: number;
    }[],
  );
  if (!binding) return [];

  const stepType = definition.stepType;

  // Video models: derive per-second cost, expanding resolution tiers when present.
  if (stepType === "video") {
    const pricingUnit = binding.pricingUnit ?? "per_second";
    const resolutionItems = getOptionItems(definition, "resolution");
    if (resolutionItems && resolutionItems.length > 1) {
      const results: PricingDetail[] = [];
      for (const item of resolutionItems) {
        const cost = estimateVideoPricing({
          binding,
          definition,
          model,
          params: { resolution: item.value },
        });
        if (cost > 0) {
          results.push(
            pricingEntry(pricingUnit, cost, { variant: item.value }),
          );
        }
      }
      if (results.length > 0) return results;
    }

    const qualityItems = getOptionItems(definition, "quality");
    if (qualityItems && qualityItems.length > 1) {
      const results: PricingDetail[] = [];
      for (const item of qualityItems) {
        const cost = estimateVideoPricing({
          binding,
          definition,
          model,
          params: { quality: item.value },
        });
        if (cost > 0) {
          results.push(
            pricingEntry(pricingUnit, cost, { variant: item.value }),
          );
        }
      }
      if (results.length > 0) return results;
    }

    const cost = estimateVideoPricing({
      binding,
      definition,
      model,
    });
    if (cost > 0) {
      return [pricingEntry(pricingUnit, cost)];
    }
  }

  // TTS: derive per-character cost
  if (stepType === "tts") {
    const sampleLength = 1000;
    const estimateFn = binding as unknown as {
      estimateCost: (p: Record<string, unknown>) => number;
    };
    try {
      const cost = estimateFn.estimateCost({
        prompt: "a".repeat(sampleLength),
      });
      if (cost > 0) {
        return [pricingEntry("per_character", cost / sampleLength)];
      }
    } catch {
      // fall through
    }
  }

  // STT: derive per-second cost
  if (stepType === "stt") {
    const estimateFn = binding as unknown as {
      estimateCost: (p: Record<string, unknown>) => number;
    };
    try {
      const cost = estimateFn.estimateCost({ prompt: "" });
      if (cost > 0) {
        return [
          pricingEntry("per_second", cost / 60, {
            note: "Estimated per second of audio",
          }),
        ];
      }
    } catch {
      // fall through
    }
  }

  // Dialogue: per-character (similar to TTS)
  if (stepType === "dialogue") {
    const sampleLength = 1000;
    const estimateFn = binding as unknown as {
      estimateCost: (p: Record<string, unknown>) => number;
    };
    try {
      const cost = estimateFn.estimateCost({
        script: "a".repeat(sampleLength),
      });
      if (cost > 0) {
        return [pricingEntry("per_character", cost / sampleLength)];
      }
    } catch {
      // fall through
    }
  }

  // Image models: expand variants by resolution or quality
  if (stepType === "image") {
    const resolutionItems = getOptionItems(definition, "resolution");
    if (resolutionItems) {
      const results: PricingDetail[] = [];
      for (const item of resolutionItems) {
        const cost = estimateStepCost({
          model,
          type: stepType,
          params: { resolution: item.value, quality: "auto" },
          prompt: "test",
        });
        if (cost > 0) {
          results.push(
            pricingEntry("per_image", cost, { variant: item.value }),
          );
        }
      }
      if (results.length > 0) return results;
    }

    const qualityItems = getOptionItems(definition, "quality");
    if (qualityItems) {
      const results: PricingDetail[] = [];
      for (const item of qualityItems) {
        const cost = estimateStepCost({
          model,
          type: stepType,
          params: { quality: item.value },
          prompt: "test",
        });
        if (cost > 0) {
          results.push(
            pricingEntry("per_image", cost, { variant: item.label }),
          );
        }
      }
      if (results.length > 0) return results;
    }

    // No variant options — single estimate
    const cost = estimateStepCost({
      model,
      type: stepType,
      params: { resolution: "1K", quality: "auto" },
      prompt: "test",
    });
    if (cost > 0) {
      return [pricingEntry("per_image", cost)];
    }
  }

  // Text / music / alignment: per-generation estimate
  if (stepType === "text" || stepType === "music" || stepType === "alignment") {
    const cost = estimateStepCost({
      model,
      type: stepType,
      params: { resolution: "1K", quality: "auto" },
      prompt: "test",
    });
    if (cost > 0) {
      return [
        pricingEntry("per_generation", cost, {
          note:
            stepType === "text"
              ? "Rough baseline estimate. Actual cost depends on token usage."
              : undefined,
        }),
      ];
    }
  }

  return [];
}

export function getAllModelPricing(): {
  models: ModelPricingEntry[];
  editor: EditorPricingEntry;
} {
  const models: ModelPricingEntry[] = [];

  for (const [model, definition] of Object.entries(modelRegistry)) {
    if (definition.spec.hidden) continue;

    const pricing = extractModelPricing(model, definition);

    models.push({
      model: definition.key,
      label: definition.spec.label,
      description: definition.spec.description,
      stepType: definition.stepType,
      pricing,
      ...(definition.referencePricing
        ? { referencePricing: definition.referencePricing }
        : {}),
    });
  }

  const editor: EditorPricingEntry = {
    type: "editor_render",
    label: "Video/Image/Audio Editor Render",
    pricing: pricingEntry("per_render", CLI_RENDER_COST_MICRODOLLARS),
  };

  return { models, editor };
}

export function estimateModelCost({
  model,
  params = {},
  prompt,
}: {
  model: string;
  params?: Record<string, unknown>;
  prompt?: string;
}): {
  estimatedCostMicrodollars: number;
  estimatedCostUsd: string;
  estimatedCredits: number;
} | null {
  if (!isModelKey(model)) return null;

  const definition = modelRegistry[model] as
    | (typeof modelRegistry)[keyof typeof modelRegistry]
    | undefined;
  if (!definition) return null;

  const binding = getPreferredProvider(
    definition.providers as unknown as {
      provider: string;
      costPerSecond?: number;
    }[],
  );
  if (!binding) return null;

  const estimateFn = binding as unknown as {
    estimateCost?: (p: Record<string, unknown>) => number;
  };

  if (typeof estimateFn.estimateCost !== "function") return null;

  try {
    const cost = estimateFn.estimateCost({ ...params, prompt: prompt ?? "" });
    if (isNaN(cost)) return null;
    const rounded = Math.round(Math.max(0, cost));
    return {
      estimatedCostMicrodollars: rounded,
      estimatedCostUsd: formatUsd(Math.max(0, cost)),
      estimatedCredits: rounded,
    };
  } catch {
    return null;
  }
}
